/**
 * Persisted SFX preference (mute) + play entry that respects mute and delegates to SoundService.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { platformStorage } from './platformStorage';
import { soundService } from '../services/SoundService';
import {
  SOUND_IDS,
  UI_SUCCESS_SOUND_DEBOUNCE_MS,
  UI_SNACKBAR_VARIANT_DEBOUNCE_MS,
  UI_TAP_SOUND_DEBOUNCE_MS,
  type SoundId,
  type SnackbarSoundVariant,
} from '../constants/sounds.constants';

/** Last `UI_SUCCESS` play (debounces dialog + snackbar chains). */
let uiSuccessSoundLastAtMs = 0;

/** Last play time per tap SFX id (debounce rapid repeats without blocking a different business sound). */
const uiTapLastAtBySoundId = new Map<SoundId, number>();

/** Last play time per snackbar variant (error / warning / info). */
const snackbarVariantLastAtMs: Partial<Record<'error' | 'warning' | 'info', number>> = {};

interface SoundPreferencesState {
  isMuted: boolean;
  toggleMute: () => void;
  setMuted: (muted: boolean) => void;
  playSound: (id: SoundId) => void;
  /** Short success tick; debounced to limit noise when snackbar follows dialog. */
  playUiSuccessBrief: () => void;
  /** One sound per snackbar variant (covers “everything” on toasts; trim later). */
  playForSnackbarVariant: (variant: SnackbarSoundVariant) => void;
  /** Pressable / tab tap (debounced per `soundId`). Defaults to generic `UI_TAP`. */
  playUiTapBrief: (soundId?: SoundId) => void;
}

export const useSoundStore = create<SoundPreferencesState>()(
  persist(
    (set, get) => ({
      isMuted: false,
      toggleMute: () => {
        const next = !get().isMuted;
        set({ isMuted: next });
        if (next) {
          void soundService.stopAll();
        }
      },
      setMuted: (muted: boolean) => {
        set({ isMuted: muted });
        if (muted) {
          void soundService.stopAll();
        }
      },
      playSound: (id: SoundId) => {
        if (get().isMuted) return;
        void soundService.play(id);
      },
      playUiSuccessBrief: () => {
        if (get().isMuted) return;
        const now = Date.now();
        if (now - uiSuccessSoundLastAtMs < UI_SUCCESS_SOUND_DEBOUNCE_MS) return;
        uiSuccessSoundLastAtMs = now;
        void soundService.play(SOUND_IDS.UI_SUCCESS);
      },
      playForSnackbarVariant: (variant: SnackbarSoundVariant) => {
        if (variant === 'success') {
          get().playUiSuccessBrief();
          return;
        }
        if (get().isMuted) return;
        const now = Date.now();
        const last = snackbarVariantLastAtMs[variant] ?? 0;
        if (now - last < UI_SNACKBAR_VARIANT_DEBOUNCE_MS) return;
        snackbarVariantLastAtMs[variant] = now;
        const id =
          variant === 'error'
            ? SOUND_IDS.UI_ERROR
            : variant === 'warning'
              ? SOUND_IDS.UI_WARNING
              : SOUND_IDS.UI_INFO;
        void soundService.play(id);
      },
      playUiTapBrief: (soundId: SoundId = SOUND_IDS.UI_TAP) => {
        if (get().isMuted) return;
        const now = Date.now();
        const last = uiTapLastAtBySoundId.get(soundId) ?? 0;
        if (now - last < UI_TAP_SOUND_DEBOUNCE_MS) return;
        uiTapLastAtBySoundId.set(soundId, now);
        void soundService.play(soundId);
      },
    }),
    {
      name: 'zporti_sound_prefs',
      storage: createJSONStorage(() => platformStorage),
      partialize: (state) => ({ isMuted: state.isMuted }),
    }
  )
);
